import { useCallback, useEffect, useState } from 'react'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { FilterChip, Select, Textarea } from '../Common'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { ConfirmActionModal } from '../Common/ConfirmActionModal'
import { Loader } from '../Common/Loader'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import {
  appendUserRule,
  getMemory,
  removeUserRule,
  updateUserRule,
} from '../../services/memoryService'
import type { UserRule, UserRuleScope } from '../../types/cognitiveProfile'

/**
 * Settings > AI Magic > User Rules.
 *
 * Renders the persisted `userRules` from the cognitive profile and lets the
 * user add, edit, or delete rules. Rules are injected into every AI prompt
 * via `injectCognitiveProfileIntoPayload` (scoped by mode).
 */
export const AiUserRulesPanel = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const { t } = useNls()
  const [rules, setRules] = useState<UserRule[]>([])
  const [loading, setLoading] = useState(true)
  const [draftText, setDraftText] = useState('')
  const [draftScope, setDraftScope] = useState<UserRuleScope>('ALL')
  const [busy, setBusy] = useState(false)
  const [ruleToDelete, setRuleToDelete] = useState<UserRule | null>(null)

  const scopeLabel: Record<UserRuleScope, string> = {
    ALL: t('NLSSettingsAiRuleScopeAll'),
    CREATE: t('NLSSettingsAiRuleScopeCreate'),
    EDIT: t('NLSSettingsAiRuleScopeEdit'),
  }

  const scopeOptions = (['ALL', 'CREATE', 'EDIT'] satisfies UserRuleScope[]).map((scope) => ({
    value: scope,
    label: scopeLabel[scope],
  }))

  const reload = useCallback(async () => {
    if (!user || user.isAnonymous) { setRules([]); setLoading(false); return }
    setLoading(true)
    try {
      const memory = await getMemory(user.uid)
      setRules(memory.userRules ?? [])
    } catch (error) {
      console.error('[ai-rules] load failed', error)
      showToast(t('NLSSettingsAiRulesLoadFailed'), 'error')
    } finally {
      setLoading(false)
    }
  }, [user, showToast, t])

  useEffect(() => {
    let ignore = false
    const fetchRules = async () => {
      if (!user || user.isAnonymous) {
        setRules([])
        setLoading(false)
        return
      }
      try {
        const memory = await getMemory(user.uid)
        if (ignore) return
        setRules(memory.userRules ?? [])
      } catch (error) {
        if (ignore) return
        console.error('[ai-rules] load failed', error)
        showToast(t('NLSSettingsAiRulesLoadFailed'), 'error')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    void fetchRules()
    return () => {
      ignore = true
    }
  }, [showToast, user, t])

  const handleAdd = async () => {
    if (!user || user.isAnonymous) return
    const text = draftText.trim()
    if (text.length < 4) {
      showToast(t('NLSSettingsAiRuleTooShort'), 'warning')
      return
    }
    setBusy(true)
    try {
      const rule = await appendUserRule(user.uid, { text, scope: draftScope, source: 'MANUAL' })
      if (!rule) {
        showToast(t('NLSSettingsAiRuleDuplicate'), 'info')
      } else {
        setDraftText('')
        await reload()
        showToast(t('NLSSettingsAiRuleSaved'), 'success')
      }
    } catch (error) {
      console.error('[ai-rules] add failed', error)
      showToast(t('NLSSettingsAiRuleSaveFailed'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = (rule: UserRule) => {
    if (!user || user.isAnonymous) return
    setRuleToDelete(rule)
  }

  const confirmDelete = async () => {
    if (!user || user.isAnonymous || !ruleToDelete) return
    setBusy(true)
    try {
      await removeUserRule(user.uid, ruleToDelete.id)
      setRuleToDelete(null)
      await reload()
    } catch (error) {
      console.error('[ai-rules] delete failed', error)
      showToast(t('NLSSettingsAiRuleDeleteFailed'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const handleScopeChange = async (rule: UserRule, nextScope: UserRuleScope) => {
    if (!user || user.isAnonymous || nextScope === rule.scope) return
    setBusy(true)
    try {
      await updateUserRule(user.uid, rule.id, { scope: nextScope })
      await reload()
    } catch (error) {
      console.error('[ai-rules] scope change failed', error)
      showToast(t('NLSSettingsAiRuleScopeFailed'), 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!user || user.isAnonymous) {
    return <div className="engagementEmpty">{t('NLSSettingsAiRulesSignInRequired')}</div>
  }

  return (
    <div className="aiRulesPanel">
      <div className="aiRulesHeader">
        <span className="aiRulesTitle">{t('NLSSettingsAiRulesSavedTitle')}</span>
        <small className="settingsFieldHint">
          {t('NLSSettingsHintYourAiRules')}
        </small>
      </div>

      <div className="aiRulesComposer">
        <Textarea
          value={draftText}
          onChange={setDraftText}
          maxLength={400}
          placeholder={t('NLSSettingsPlaceholderNewRule')}
          rows={2}
          showCount={false}
          aria-label={t('NLSSettingsLabelNewRule')}
        />
        <div className="aiRulesComposerFooter">
          <div className="aiRulesScopeGroup" role="radiogroup" aria-label={t('NLSSettingsAiRuleScopeGroupAria')}>
            {(['ALL', 'CREATE', 'EDIT'] satisfies UserRuleScope[]).map((scope) => (
              <FilterChip
                key={scope}
                selectionRole="radio"
                selected={draftScope === scope}
                onClick={() => setDraftScope(scope)}
              >
                {scopeLabel[scope]}
              </FilterChip>
            ))}
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || draftText.trim().length < 4}
            onClick={() => void handleAdd()}
          >{t('NLSSettingsActionAddRule')}</Button>
        </div>
      </div>

      {loading ? (
        <div className="aiRulesEmpty"><Loader size={140} label="Loading…" /></div>
      ) : rules.length === 0 ? (
        <div className="aiRulesEmpty">{t('NLSSettingsAiRulesEmpty')}</div>
      ) : (
        <ul className="aiRulesList">
          {rules.map((rule) => (
            <li key={rule.id} className="aiRulesItem">
              <div className="aiRulesItemBody">
                <p className="aiRulesItemText">{rule.text}</p>
                <small className="aiRulesItemMeta">
                  {rule.source === 'DETECTED' ? t('NLSSettingsAiRuleAutoDetected') : t('NLSSettingsAiRuleManuallySaved')}
                </small>
              </div>
              <div className="aiRulesItemControls">
                <Select<UserRuleScope>
                  value={rule.scope}
                  disabled={busy}
                  onChange={(next) => void handleScopeChange(rule, next)}
                  ariaLabel={`Scope for rule ${rule.text}`}
                  label={t('NLSSettingsAiRuleScope')}
                  trigger="labeledPill"
                  options={scopeOptions}
                />
                <IconButton
                  size="sm"
                  variant="tertiary"
                  tone="danger"
                  className="aiRulesDeleteButton"
                  icon={<DeleteOutlineRoundedIcon fontSize="small" />}
                  label={t('NLSSettingsAiRuleDelete')}
                  aria-label={`Delete rule ${rule.text}`}
                  disabled={busy}
                  onClick={() => handleDelete(rule)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmActionModal
        open={Boolean(ruleToDelete)}
        danger
        title={t('NLSSettingsAiRuleDeleteTitle')}
        body={`Delete rule "${ruleToDelete?.text}"?`}
        confirmLabel={t('NLSStockActionDelete')}
        busy={busy}
        onClose={() => setRuleToDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  )
}
